import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { Component, Input } from "@angular/core";
import * as _ from "lodash";
import { NgxSpinnerService } from "ngx-spinner";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { EntityService } from "../services/entity.service";
import { PositionService } from "../services/position.service";
import { PpsUtilityService } from "../services/pps-utility.service";
import { RosterTypeService } from "../services/roster-type.service";
import { WorkGroupPositionRosterTypeService } from "../services/workgroup-position-roster.service";
import { IEntity } from "../shared/models/entity";
import { Position } from "../shared/models/position";
import { IRosterType } from "../shared/models/roster/roster-type";
import { IWorkGroupPositionRosterType } from "../shared/models/roster/workgroup-position-rostertype";
import { ToastService } from "../shared/toast/toast.service";
import { DeleteActionComponent } from "./delete-action.component";
import { WorkGroupPositionRosterTypeFormComponent } from "./workgroup-position-rostertype-form.component";

@Component({
  selector: "pps-manage-workgroup-position-rostertype",
  templateUrl: "./manage-workgroup-position-rostertype.component.html"
})
export class ManageWorkGroupPositionRosterType{
  private subs = new SubSink();
  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  @Input() data: IWorkGroupPositionRosterType[] = [];
  @Input() workGroups: IEntity[] = [];
  @Input() positions: Position[] = [];
  @Input() rosterTypes: IRosterType[] = [];

  private gridApi;
  private gridColumnApi;
  gridHeaders: any[];
  context:any = {};
  gridOptions: any = {
    getRowId: param => `${param.data.workGroup.id}-${param.data.position.id}`,
    defaultColDef:{
      resizable: true
    }
  };  

  constructor(
    private _workGroupPositionRosterTypeService: WorkGroupPositionRosterTypeService,
    private _entityService: EntityService,
    private _positionService: PositionService,
    private _rosterTypeService: RosterTypeService,
    private _spinner: NgxSpinnerService,
    private _toast: ToastService,
    private _dialog: DialogService,
    private _confirm: ConfirmationService,
    private _util: PpsUtilityService,
    private breakpointObserver: BreakpointObserver){
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });      
  }

  ngOnInit(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadAllData();
  }
  
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  removable(item: IWorkGroupPositionRosterType){
    return true;
  }

  loadData(){
    this._spinner.show(this.spinnerName);
    this.data.length = 0;
    this.subs.sink = forkJoin([
      this._workGroupPositionRosterTypeService.getAll()
    ]).subscribe(([res]) => {
      if (res.succeed){
        this.data = res.result;
      }
    })
    .add(()=>{
      this._spinner.hide(this.spinnerName);
    });
  }

  loadAllData(){
    this.workGroups = this.workGroups.filter( wg => wg.active);
    this.workGroups = this.workGroups.sort((a: IEntity, b: IEntity) => (a.name < b.name) ? -1 : (a.name > b.name ? 1 : 0));
    this.positions = _.cloneDeep(this.positions).sort((a: Position, b: Position) => (a.title < b.title) ? -1 : (a.title > b.title ? 1 : 0));
    this.rosterTypes = _.cloneDeep(this.rosterTypes).sort((a: IRosterType, b: IRosterType) => (a.title < b.title) ? -1 : (a.title > b.title ? 1 : 0));
  }  

  addItem(){
    let dialog = this._dialog.open(WorkGroupPositionRosterTypeFormComponent, {
      header: `Configure Roster Type by Work Group and Position`,
      styleClass: "size-md",
      data:{
        workGroups: this.workGroups,
        positions: this.positions,
        rosterTypes: this.rosterTypes
      },
      draggable: true
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      items => {
        if (items){
          this.subs.sink = this._workGroupPositionRosterTypeService.add(items)
          .subscribe(res => {
            if (res.succeed){
              this.loadData();
            }
          }, _error => {
            console.log(_error);
          })
          .add(()=>{
            this._spinner.hide(this.spinnerName);
          });
        }
      },
      reason => {
      }
    );
  }

  delete(item: IWorkGroupPositionRosterType){
    if (item === null){
      return;
    }

    this._confirm.confirm({
      key: "wg-p-rt-dialog",
      header: `Delete Data`,
      message: `Please confirm that you want to delete this item [${item.workGroup.name}, ${item.position.title}, ${item.rosterType.title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(item)
    });
  }

  deleteHelper(item: IWorkGroupPositionRosterType){
    this.subs.sink = this._workGroupPositionRosterTypeService.delete(item)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.data.findIndex(x => x.workGroup.id == item.workGroup.id && 
            x.position.id === item.position.id && 
            x.rosterType.id === item.rosterType.id);

          if (foundIndex !== -1 ){
            this.data.splice(foundIndex, 1);
            let rowNode = this.gridApi.getRowNode(`${item.workGroup.id}-${item.position.id}`);
            if (rowNode){
              this.gridApi.flashCells({rowNodes: [rowNode], flashDelay: 1000, fadeDelay: 1000});
              window.setTimeout(() => {
                this.gridApi.setRowData(this.data);
                this._toast.succeeded(`[${item.workGroup.name}, ${item.position.title}, ${item.rosterType.title}] deleted!`);
              }, 1000)
            }
          }else{
            this._toast.failed(`Delete [${item.workGroup.name}, ${item.position.title}, ${item.rosterType.title}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${item.workGroup.name}, ${item.position.title}, ${item.rosterType.title}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }


  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"}
      },
      {
        colId: "workGroup",
        headerName: 'Work Group',
        field:'workGroup.name',
        filter: true,
        floatingFilter: true,
        width: 300,
        sortable: true,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "position",
        headerName: 'Position',
        field:'position.title',
        filter: true,
        floatingFilter: true,
        width: 300,
        sortable: true,
        headerClass: "ag-ext-header-align-left"
      },
      {
        colId: "rosterType",
        headerName: 'Roster Type',
        field:'rosterType.title',
        filter: true,
        floatingFilter: true,
        width: 300,
        sortable: true,
        headerClass: "ag-ext-header-align-left"
      }
    ];

    return cols;
  }
}